import type { Locale } from "@/lib/i18n";

interface StudioCopy {
  headingLead: string;
  headingMiddle: string;
  headingAccent: string;
  intro: string;
  previousExperience: string;
  article: string;
  allWriting: string;
  contributionsLead: string;
  contributionsAccent: string;
  contributionsIntro: string;
  companyHeading: string;
  closingEyebrow: string;
  closingLead: string;
  closingAccent: string;
  closingIntro: string;
  profiles: {
    intro: string;
    careerLabel: string;
    storyLabel: string;
    metrics: { value: string; unit?: string; label: string }[];
  }[];
}

// Personal career figures supplied on 18 October 2026; funding corrected on 19 October.
// Funding reviewed is not funding approved, secured or disbursed.
export const peopleStudioCopy: Record<Locale, StudioCopy> = {
  en: {
    headingLead: "Experienced.",
    headingMiddle: "Still",
    headingAccent: "curious.",
    intro:
      "We build AI agents, help teams adopt new tools, and share what we learn.",
    previousExperience: "Previous experience",
    article: "Article",
    allWriting: "All writing",
    contributionsLead: "Ideas out in the",
    contributionsAccent: "world.",
    contributionsIntro:
      "We write, speak and contribute to the community, sharing what we learn along the way.",
    companyHeading: "The company behind the people.",
    closingEyebrow: "Let’s work together",
    closingLead: "Let’s build something",
    closingAccent: "useful.",
    closingIntro:
      "Have a project in mind, a team to train, or a partnership to explore? Let’s talk.",
    profiles: [
      {
        intro:
          "Jazz has built software for 18 years. At Anchor Sprint, he leads software delivery, AI agent development and data integration.",
        careerLabel: "Across his engineering career",
        storyLabel: "Read Jazz’s full story",
        metrics: [
          {
            value: "10M",
            label: "Monthly active users served by systems built",
          },
          { value: "1,000+", label: "Production servers managed" },
          { value: "200+", label: "Incidents handled" },
        ],
      },
      {
        intro:
          "Jee Fong combines banking experience with business evaluation, partnerships and AI training. She has reviewed RM1 billion in capital funding and evaluated more than 500 businesses.",
        careerLabel: "Across her banking career",
        storyLabel: "Read Jee Fong’s full story",
        metrics: [
          { value: "RM1B", label: "Capital funding reviewed" },
          { value: "500+", label: "Businesses evaluated" },
        ],
      },
    ],
  },
  ms: {
    headingLead: "Berpengalaman.",
    headingMiddle: "Terus",
    headingAccent: "ingin tahu.",
    intro:
      "Kami membina ejen AI, membantu pasukan menggunakan alat baharu dan berkongsi apa yang kami pelajari.",
    previousExperience: "Pengalaman terdahulu",
    article: "Artikel",
    allWriting: "Semua penulisan",
    contributionsLead: "Idea dikongsi bersama",
    contributionsAccent: "dunia.",
    contributionsIntro:
      "Kami menulis, berucap dan menyumbang kepada komuniti sambil berkongsi apa yang kami pelajari.",
    companyHeading: "Syarikat di sebalik pasukan ini.",
    closingEyebrow: "Mari bekerjasama",
    closingLead: "Mari bina sesuatu yang",
    closingAccent: "berguna.",
    closingIntro:
      "Ada projek, keperluan latihan pasukan atau peluang kerjasama? Mari berbincang.",
    profiles: [
      {
        intro:
          "Jazz membina perisian selama 18 tahun. Di Anchor Sprint, beliau mengetuai pembangunan perisian, ejen AI dan penyepaduan data.",
        careerLabel: "Sepanjang kerjaya kejuruteraannya",
        storyLabel: "Baca kisah penuh Jazz",
        metrics: [
          {
            value: "10M",
            label: "Pengguna aktif bulanan bagi sistem yang dibina",
          },
          { value: "1,000+", label: "Pelayan produksi diurus" },
          { value: "200+", label: "Insiden ditangani" },
        ],
      },
      {
        intro:
          "Jee Fong menggabungkan pengalaman perbankan dengan penilaian perniagaan, perkongsian dan latihan AI. Beliau menyemak pembiayaan modal RM1 bilion dan menilai lebih 500 perniagaan.",
        careerLabel: "Sepanjang kerjaya perbankannya",
        storyLabel: "Baca kisah penuh Jee Fong",
        metrics: [
          { value: "RM1B", label: "Pembiayaan modal disemak" },
          { value: "500+", label: "Perniagaan dinilai" },
        ],
      },
    ],
  },
  zh: {
    headingLead: "经验深厚，",
    headingMiddle: "",
    headingAccent: "求知不止。",
    intro: "我们构建 AI 智能体，帮助团队使用新工具，并分享实践中的收获。",
    previousExperience: "过往任职",
    article: "文章",
    allWriting: "全部文章",
    contributionsLead: "让想法走向",
    contributionsAccent: "世界。",
    contributionsIntro: "我们通过写作、演讲和社群活动，分享实践中学到的知识。",
    companyHeading: "团队背后的公司。",
    closingEyebrow: "与我们合作",
    closingLead: "一起创造",
    closingAccent: "实用价值。",
    closingIntro: "无论是项目、团队培训还是合作机会，欢迎与我们交流。",
    profiles: [
      {
        intro:
          "Jazz 已从事软件开发十八年。在安克极速，他负责软件交付、AI 智能体开发和数据集成。",
        careerLabel: "工程职业生涯积累",
        storyLabel: "阅读 Jazz 的完整经历",
        metrics: [
          { value: "10M", label: "所构建系统服务的月活跃用户" },
          { value: "1,000+", label: "管理过的生产服务器" },
          { value: "200+", label: "处理过的事故" },
        ],
      },
      {
        intro:
          "Jee Fong 将银行业经验与企业评估、合作伙伴关系及 AI 培训结合起来。她审查过十亿令吉的资本融资，并评估过超过五百家企业。",
        careerLabel: "银行职业生涯积累",
        storyLabel: "阅读 Jee Fong 的完整经历",
        metrics: [
          { value: "RM1B", label: "审查过的资本融资" },
          { value: "500+", label: "评估过的企业" },
        ],
      },
    ],
  },
};
